import React, { useState, useEffect, useRef } from "react";
import "../assets/style/gemini.css";
import { Button } from "react-bootstrap";
import ReactMarkdown from "react-markdown";

interface GeminiRequestProps {
  text?: string;
}

const GeminiRequest: React.FC<GeminiRequestProps> = ({ text = "" }) => {
  const [prompt, setPrompt] = useState(text);
  const [output, setOutput] = useState("");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const outputRef = useRef<HTMLDivElement>(null);

  // Actualiza el textarea si la propiedad text cambia desde fuera
  useEffect(() => {
    setPrompt(text);
  }, [text]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!prompt.trim()) return;

    setLoading(true);
    setOutput("");
    setCopied(false);

    try {
      const API_BASE =
        process.env.NODE_ENV === "development"
          ? "http://localhost:5500"
          : "https://ligavirtualf1.es:5500";
      const response = await fetch(`${API_BASE}/api/gemini/getText`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ prompt }),
      });

      const data = await response.json();
      setOutput(data.response || "No se ha recibido respuesta.");
    } catch (error) {
      console.error("Error al consultar el backend:", error);
      setOutput("Hubo un error al conectar con el servidor.");
    } finally {
      setLoading(false);
    }
  };

  // Función mejorada para copiar manteniendo el formato enriquecido hacia Word
  const handleCopy = async () => {
    if (!outputRef.current || !output) return;
    try {
      const htmlContent = outputRef.current.innerHTML;
      const textContent = outputRef.current.innerText;

      // Usar la API del portapapeles para inyectar tanto HTML (para Word) como Texto Plano
      const clipboardItem = new ClipboardItem({
        "text/html": new Blob([htmlContent], { type: "text/html" }),
        "text/plain": new Blob([textContent], { type: "text/plain" }),
      });

      await navigator.clipboard.write([clipboardItem]);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback por seguridad si el navegador bloquea ClipboardItem
      try {
        await navigator.clipboard.writeText(output);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (fallbackErr) {
        console.error("Error al copiar al portapapeles:", fallbackErr);
      }
    }
  };

  return (
    <div>
      <form onSubmit={handleSubmit} className="gemini-form">
        <label>Input text:</label>
        <textarea
          className="gemini-textarea"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          rows={15}
          placeholder="Type what you want to send to Gemini..."
        />
        <div className="text-center">
          <Button variant="primary" type="submit">
            {loading ? "Generating..." : "Send to Gemini"}
          </Button>
        </div>
      </form>

      <div className="gemini-output-container">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>Output response:</span>
          {output && !loading && (
            <Button variant="secondary" size="sm" onClick={handleCopy}>
              {copied ? "Copied with format!" : "Copy"}
            </Button>
          )}
        </div>
        <div className="gemini-output-box">
          {loading ? (
            <p className="gemini-loading-text">Waiting for response...</p>
          ) : (
            // Contenedor con ref que renderiza el Markdown con estilos visuales limpios
            <div ref={outputRef} className="gemini-markdown-output">
              {output ? (
                <ReactMarkdown>{output}</ReactMarkdown>
              ) : (
                <p className="text-muted">The response will appear here...</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GeminiRequest;
